import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppShell, Content } from '../../components/ui/AppShell';
import { ChevronLeft, MoreHorizontal, Bell } from '../../components/ui/icons';
import { useAuth } from '../../context/AuthContext';
import {
  useLeagues,
  useCurrentRound,
  useMyEntry,
  useMyMembership,
  useUnreadByLeague
} from '../../hooks/useQueries';
import { identityFor, isLeagueActive } from '../../data/leagues';
import { tierLabel } from '../../lib/format';
import { LeagueSwitcher } from '../../components/ui/LeagueSwitcher';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { SegmentedViews } from '../../components/ui/SegmentedViews';
import { useHaptics } from '../../hooks/useHaptics';
import { shareWithFeedback } from '../../lib/share';
import { CompeteTab } from '../play/CompeteTab';
import { FixturesTab } from '../play/FixturesTab';
import { StagesTab } from '../play/StagesTab';
import { TableTab } from '../play/TableTab';
import { HubTabBackToCompete, HubTabHint } from '../play/compete/LeagueHubGuide';
import { hubLabels } from '../play/compete/hubLabels';
import { LeagueWaiting } from '../play/LeagueWaiting';
import { LeagueMenuSheet } from './LeagueMenuSheet';
import { parseTab, type Tab } from './types';

/**
 * The per-league hub ("League home"). A shared header plus a segmented control with
 * three tabs for every sport:
 *   Compete  — build cards (football) or call duels (cycling), then your division snap.
 *   Fixtures / Stages — the round schedule with your cards marked.
 *   Table    — real competition standings (separate from your HeadToHead division).
 * The richer locked-board and results recaps stay on their own routes, reachable
 * from the Compete CTA.
 */
export default function RoundPlay() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { leagueId } = useParams<{ leagueId: string }>();
  const [params, setParams] = useSearchParams();
  const { uid } = useAuth();

  const { data: leagues } = useLeagues();
  const league = leagues?.find((l) => l.leagueId === leagueId);
  const identity = identityFor(leagueId ?? '');
  const name = league?.name ?? identity?.name ?? 'League';
  const country = league?.country ?? identity?.country ?? '';
  const roundId = league?.currentRoundId ?? null;

  const { data: round } = useCurrentRound(league?.leagueId, roundId);
  const { data: entry } = useMyEntry(league?.leagueId, roundId, uid);
  const { data: membership } = useMyMembership(leagueId, uid);
  const unread = useUnreadByLeague(uid)[leagueId ?? ''] ?? 0;
  const sport = league?.sport ?? 'FOOTBALL';
  const labels = hubLabels(sport);

  const [menuOpen, setMenuOpen] = useState(false);

  // Overflow-menu actions: competition-scoped things the bottom tab bar doesn't
  // already cover one-tap (the tabs handle Standings/Friends/Profile).
  const inviteFriends = async () => {
    impact('light');
    setMenuOpen(false);
    const url = typeof window !== 'undefined' ? window.location.origin : '';
    await shareWithFeedback({
      title: name,
      text: sport === 'CYCLING'
        ? `Join me on HeadToHead for ${name} — predict the rider duels on every stage and climb your division.`
        : `Join me on HeadToHead for ${name} — build your cards every match-day and climb your division.`,
      url
    });
  };
  const goMiniLeagues = () => {
    impact('light');
    setMenuOpen(false);
    navigate('/leagues', { state: { from: 'play', leagueId: leagueId ?? undefined } });
  };
  const goStandings = () => {
    impact('light');
    setMenuOpen(false);
    navigate(`/play/${leagueId}/standings`);
  };
  const goBuildCards = () => {
    impact('medium');
    setMenuOpen(false);
    const p = new URLSearchParams(params);
    p.delete('tab');
    setParams(p, { replace: true });
  };
  const goLiveBoard = () => {
    impact('medium');
    setMenuOpen(false);
    if (sport === 'CYCLING') {
      navigate(`/play/${leagueId}/standings`);
    } else {
      navigate(`/play/${leagueId}/waiting`);
    }
  };
  const roundOpen = round?.status === 'OPEN';
  const goNotifications = () => {
    impact('light');
    setMenuOpen(false);
    navigate(leagueId ? `/play/${leagueId}/notifications` : '/notifications');
  };

  // Same three tabs for every sport; map the middle tab key per sport (fixtures vs stages).
  const rawTab = parseTab(params.get('tab'));
  const tab: Tab =
    rawTab === 'table'
      ? 'table'
      : rawTab === labels.scheduleTabKey
        ? labels.scheduleTabKey
        : 'compete';
  const setTab = (next: Tab) => {
    impact('selection');
    const p = new URLSearchParams(params);
    if (next === 'compete') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
  };

  const tierName = membership != null ? `${tierLabel(membership.tier).replace(' tier', '')} division` : 'Your division';
  const hubSubtitle =
    sport === 'CYCLING' && round?.label
      ? `${round.label} · ${tierName}`
      : `${country ? `${country} · ` : ''}${tierName}`;

  // Only active competitions are playable right now — everything else is between
  // seasons. Send the player to a clear waiting state rather than a stale board.
  if (leagueId && !isLeagueActive(leagueId)) {
    return <LeagueWaiting leagueId={leagueId} name={name} country={country} />;
  }

  return (
    <AppShell>
      <Content width="wide">
        {/* nav */}
        <div className="flex items-center justify-between pt-2 md:pt-5">
          <button
            type="button"
            onClick={() => {
              impact('light');
              navigate('/');
            }}
            className="tap -ml-1 flex items-center gap-[2px] text-ink"
          >
            <ChevronLeft width={22} height={22} strokeWidth={2.4} />
            <span className="text-[16px] font-semibold tracking-snug">Home</span>
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Notifications"
              onClick={goNotifications}
              className="tap relative grid h-10 w-10 place-items-center rounded-full border border-black/5 bg-white text-ink shadow-soft"
            >
              <Bell width={18} height={18} strokeWidth={2} />
              {unread > 0 && (
                <span className="absolute right-[7px] top-[7px] h-[8px] w-[8px] rounded-full border-[1.5px] border-white bg-accent" />
              )}
            </button>
            <button
              type="button"
              aria-label="Competition options"
              onClick={() => {
                impact('light');
                setMenuOpen(true);
              }}
              className="tap grid h-10 w-10 place-items-center rounded-full border border-black/5 bg-white text-ink shadow-soft"
            >
              <MoreHorizontal width={18} height={18} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* league header — tap the crest/name to switch worlds */}
        <div className="pt-3.5">
          <LeagueSwitcher
            leagueId={leagueId!}
            tab={null}
            subtitle={hubSubtitle}
          />
        </div>

        {/* Same three-tab hub for every sport: Compete · schedule · real table. */}
        <div className="pt-4">
          <SegmentedControl
            value={tab}
            onChange={setTab}
            layoutId="round-play-tab-pill"
            options={[
              { key: 'compete', label: 'Compete' },
              { key: labels.scheduleTabKey, label: labels.scheduleTab },
              { key: 'table', label: labels.tableTab }
            ]}
          />
        </div>

        {/* Football's Compete tab is the build-your-cards flow, which carries its
            own combined header — the generic hint would just repeat it. Cycling's
            open stage does the same for duel calling + ranking. The body cross-fades
            in sync with the segmented pill so content never hard-cuts under it. */}
        <SegmentedViews value={tab} order={['compete', labels.scheduleTabKey, 'table']}>
          {tab !== 'compete' && <HubTabHint tab={tab} sport={sport} />}

          {tab === 'compete' && (
            <CompeteTab
              leagueId={leagueId!}
              sport={league?.sport}
              nationalTeams={league?.country === 'World'}
              scorerEnabled={league?.scorerEnabled !== false}
              statsEnabled={league?.statsEnabled === true}
              round={round}
              entry={entry}
              membership={membership}
              uid={uid}
              tierIndex={membership?.tier ?? null}
              onOpenTab={setTab}
              onPredict={(action, rid, hasEntry) => {
                impact('medium');
                if (action === 'results') {
                  const q = rid && rid !== roundId ? `?round=${rid}` : '';
                  navigate(`/play/${leagueId}/results${q}`);
                } else if (action === 'board') {
                  if (sport === 'CYCLING') {
                    navigate(`/play/${leagueId}/standings`);
                  } else {
                    const q = rid && rid !== roundId ? `?round=${rid}` : '';
                    navigate(hasEntry ? `/play/${leagueId}/waiting${q}` : `/play/${leagueId}/standings`);
                  }
                } else {
                  const q = rid && rid !== roundId ? `?round=${rid}` : '';
                  navigate(`/play/${leagueId}${q}`);
                }
              }}
              onOpenFixture={(fixtureId, rid) => {
                impact('light');
                const r = rid ?? roundId;
                if (sport === 'CYCLING') {
                  const q = new URLSearchParams();
                  if (r && r !== roundId) q.set('round', r);
                  q.set('duel', fixtureId);
                  const qs = q.toString();
                  navigate(`/play/${leagueId}${qs ? `?${qs}` : ''}`);
                } else {
                  navigate(`/play/${leagueId}/fixture/${fixtureId}${r ? `?round=${r}` : ''}`);
                }
              }}
              onInvite={inviteFriends}
              onFullTable={() => navigate(`/play/${leagueId}/standings`)}
            />
          )}

          {sport === 'CYCLING' && tab === 'stages' && (
            <>
              <StagesTab leagueId={leagueId!} currentRoundId={roundId} uid={uid} />
              <HubTabBackToCompete sport={sport} onClick={() => setTab('compete')} />
            </>
          )}

          {sport !== 'CYCLING' && tab === 'fixtures' && (
            <>
              <FixturesTab
                leagueId={leagueId!}
                currentRoundId={roundId}
                uid={uid}
                nationalTeams={league?.country === 'World'}
              />
              <HubTabBackToCompete sport={sport} onClick={() => setTab('compete')} />
            </>
          )}

          {tab === 'table' && (
            <>
              <TableTab leagueId={leagueId!} season={league?.season} sport={sport} />
              <HubTabBackToCompete sport={sport} onClick={() => setTab('compete')} />
            </>
          )}
        </SegmentedViews>
      </Content>

      <LeagueMenuSheet
        open={menuOpen}
        leagueName={name}
        isCycling={sport === 'CYCLING'}
        roundOpen={roundOpen}
        hasEntry={!!entry}
        onClose={() => setMenuOpen(false)}
        onInvite={inviteFriends}
        onMiniLeagues={goMiniLeagues}
        onStandings={goStandings}
        onBuildCards={goBuildCards}
        onLiveBoard={goLiveBoard}
      />
    </AppShell>
  );
}
